import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Clock3 } from "lucide-react";
import { StatusPill } from "@ocs/ui";
import { adminApi } from "../api";
import { DataTable } from "../components/DataTable";
import { amount, shortAddress } from "../format";

type Settlement = {
  period_id: string; period_start_at: string; period_end_at: string; total_fee_income: string;
  distributable_amount: string; eligible_user_count: string; amount_per_user: string;
  rounding_remainder: string; batch_status: number; confirmation_status: number | null;
  settlement_payload_hash: string | null; settlement_merkle_root: string | null;
  owner_address: string | null; window_start_at: string | null; window_end_at: string | null; tx_hash: string | null;
  transactions: SettlementTransaction[];
};

type SettlementTransaction = {
  confirmation_id: string;
  contract_name: string;
  contract_address: string;
  confirmation_status: number;
  tx_hash: string | null;
  block_number: string | null;
  confirmed_at: string | null;
  pool_fee_income: string;
  pool_distributable_amount: string;
};

const poolName = (name: string) => name === "FeeDividendContract"
  ? "节点手续费池"
  : name === "MarketNodeFeeDividendPool"
    ? "Market 手续费池"
    : name;

const confirmationLabel = (status: number, txHash: string | null) => {
  if (status === 3) return { label: "链上已确认", tone: "success" as const };
  if (status >= 4) return { label: "确认失败", tone: "danger" as const };
  return { label: txHash ? "链上确认中" : "等待提交", tone: "warning" as const };
};

const columns: ColumnDef<Settlement>[] = [
  { header: "周期", accessorKey: "period_id", cell: ({ getValue }) => <code>#{getValue<string>()}</code> },
  { header: "统计区间", cell: ({ row }) => `${new Date(row.original.period_start_at).toLocaleDateString("zh-CN")} — ${new Date(row.original.period_end_at).toLocaleDateString("zh-CN")}` },
  { header: "手续费 / 30%", cell: ({ row }) => <span className="numeric">{amount(row.original.total_fee_income)} / {amount(row.original.distributable_amount)}</span> },
  { header: "有效节点", accessorKey: "eligible_user_count" },
  { header: "每人分红", accessorKey: "amount_per_user", cell: ({ getValue }) => <b className="numeric">{amount(getValue<string>())}</b> },
  { header: "自动发放", accessorKey: "batch_status", cell: ({ getValue }) => { const value = getValue<number>(); return <StatusPill tone={value === 2 ? "success" : value === 3 ? "danger" : "warning"}>{value === 2 ? "已全部到账" : value === 3 ? "部分发放失败" : "发放处理中"}</StatusPill>; } },
  {
    header: "交易",
    cell: ({ row }) => {
      const transactions = row.original.transactions ?? [];
      if (transactions.length === 0) return <span className="settlement-transactions__empty">等待提交</span>;

      return (
        <details className="settlement-transactions">
          <summary>{transactions.length}笔交易</summary>
          <div className="settlement-transaction-list">
            {transactions.map((transaction) => {
              const status = confirmationLabel(Number(transaction.confirmation_status), transaction.tx_hash);
              return (
                <article key={transaction.confirmation_id}>
                  <header>
                    <div>
                      <strong>{poolName(transaction.contract_name)}</strong>
                      <code>{shortAddress(transaction.contract_address)}</code>
                    </div>
                    <StatusPill tone={status.tone}>{status.label}</StatusPill>
                  </header>
                  <dl>
                    <div><dt>手续费</dt><dd>{amount(transaction.pool_fee_income)} USDT</dd></div>
                    <div><dt>分红金额</dt><dd>{amount(transaction.pool_distributable_amount)} USDT</dd></div>
                    <div><dt>确认区块</dt><dd>{transaction.block_number ? `#${transaction.block_number}` : "—"}</dd></div>
                  </dl>
                  {transaction.tx_hash
                    ? <a href={`https://bscscan.com/tx/${transaction.tx_hash}`} target="_blank" rel="noreferrer"><code>{shortAddress(transaction.tx_hash)}</code><span>在 BscScan 查看</span></a>
                    : <span className="settlement-transactions__empty">尚无交易哈希</span>}
                </article>
              );
            })}
          </div>
        </details>
      );
    }
  }
];

export function SettlementPage() {
  const query = useQuery({ queryKey: ["settlements"], queryFn: () => adminApi<Settlement[]>("/settlements"), refetchInterval: 5000 });
  return (
    <div className="settlement-page">
      <section className="owner-boundary">
        <Clock3 />
        <div><strong>每周日 00:00–01:00 自动结算</strong><span>系统按上一周期手续费池的 30% 计算分红，并在上海时间每周日 00:00–01:00 自动发送到满足条件的用户钱包，无需用户申请提现。</span></div>
      </section>
      <DataTable data={query.data ?? []} columns={columns} emptyMessage="首个周日 00:00 自动分红后，这里会显示结算与到账进度。" />
    </div>
  );
}
